import { useEffect, useState } from 'react'
import { useAppSelector } from '@/store'

/**
 * Feature flags driven by boolean Firebase Remote Config parameters. Without
 * REACT_APP_FIREBASE_WEB_APP_CONFIG every flag is off.
 *
 * Targeting lives in the Firebase console as conditions on the parameter. The
 * logged-in employee is sent as custom signal `cashy_employee_id`, so a
 * condition can enable a flag for specific employees. Signals are set by the
 * browser, so don't use them for anything that must be enforced.
 *
 * Manual override for QA: localStorage `cashy_feature_flag_<flag>` = 'on' | 'off'.
 */

export type FeatureFlag = 'admin_deals_list_items_columns_enabled'

const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean> = {
  admin_deals_list_items_columns_enabled: false,
}

const MINIMUM_FETCH_INTERVAL_MS = 5 * 60 * 1000
const FETCH_TIMEOUT_MS = 5 * 1000
const FETCHED_FOR_EMPLOYEE_STORAGE_KEY = 'cashy_feature_flags_employee_id'

function getManualOverride(flag: FeatureFlag): boolean | undefined {
  try {
    const value = window.localStorage.getItem(`cashy_feature_flag_${flag}`)
    if (value === 'on') return true
    if (value === 'off') return false
  } catch {
    // ignore
  }
  return undefined
}

function getPersistedEmployeeId(): string | null {
  try {
    const user = JSON.parse(window.localStorage.getItem('user') ?? 'null')
    return user?._id ?? null
  } catch {
    return null
  }
}

const remoteConfigPromises = new Map<
  string,
  Promise<typeof import('firebase/remote-config') | undefined>
>()

// Fetched at most once per page load and employee, shared by all flags.
function loadRemoteConfig(employeeId: string | null) {
  const cacheKey = employeeId ?? ''
  let promise = remoteConfigPromises.get(cacheKey)

  if (!promise) {
    promise = (async () => {
      const firebaseWebAppConfig = process.env.REACT_APP_FIREBASE_WEB_APP_CONFIG
      if (!firebaseWebAppConfig) {
        return undefined
      }

      const { getApp, getApps, initializeApp } = await import('firebase/app')
      const remoteConfigModule = await import('firebase/remote-config')
      const app = getApps().length
        ? getApp()
        : initializeApp(JSON.parse(firebaseWebAppConfig))
      const remoteConfig = remoteConfigModule.getRemoteConfig(app)
      remoteConfig.settings.fetchTimeoutMillis = FETCH_TIMEOUT_MS
      remoteConfig.defaultConfig = FEATURE_FLAG_DEFAULTS

      // null deletes the signal, e.g. no employee logged in
      await remoteConfigModule.setCustomSignals(remoteConfig, {
        cashy_employee_id: employeeId,
      })

      // Cached values were fetched for another employee (e.g. a different
      // login in this browser) → bypass the fetch interval once.
      const fetchedForEmployeeId = window.localStorage.getItem(
        FETCHED_FOR_EMPLOYEE_STORAGE_KEY,
      )
      remoteConfig.settings.minimumFetchIntervalMillis =
        fetchedForEmployeeId === cacheKey ? MINIMUM_FETCH_INTERVAL_MS : 0

      await remoteConfigModule.fetchAndActivate(remoteConfig)
      window.localStorage.setItem(FETCHED_FOR_EMPLOYEE_STORAGE_KEY, cacheKey)
      return remoteConfigModule
    })().catch((error) => {
      console.error('Failed to load feature flags', error)
      remoteConfigPromises.delete(cacheKey)
      return undefined
    })
    remoteConfigPromises.set(cacheKey, promise)
  }
  return promise
}

export async function isFeatureEnabled(
  flag: FeatureFlag,
  employeeId: string | null,
): Promise<boolean> {
  const override = getManualOverride(flag)
  if (override !== undefined) {
    return override
  }

  const remoteConfigModule = await loadRemoteConfig(employeeId)
  if (!remoteConfigModule) {
    return FEATURE_FLAG_DEFAULTS[flag]
  }

  return remoteConfigModule.getBoolean(
    remoteConfigModule.getRemoteConfig(),
    flag,
  )
}

/** `undefined` while the flag is being resolved. */
export function useFeatureFlag(flag: FeatureFlag): boolean | undefined {
  const currentEmployeeId = useAppSelector(
    (state) => state.user.current?._id as string | undefined,
  )
  // The store is restored asynchronously after a reload
  const employeeId = currentEmployeeId ?? getPersistedEmployeeId()

  const [enabled, setEnabled] = useState<boolean | undefined>(
    getManualOverride(flag),
  )

  useEffect(() => {
    let cancelled = false
    isFeatureEnabled(flag, employeeId).then((value) => {
      if (!cancelled) setEnabled(value)
    })
    return () => {
      cancelled = true
    }
  }, [flag, employeeId])

  return enabled
}
